import { DataGridConfig } from 'components/DataGrid';
import { useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { truncateText } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect, useMemo, useState } from 'react';

import RowActions from './RowActions';
import {
  findingsDataQueryParamsAtom,
  orderbyAtom,
  paginationConfigAtom,
  selectedRowIdsAtom,
  sortOrderAtom,
  columnKeysPCAtom,
  DEFAULT_COLUMN_KEYS_PC,
  findingsDataAtom,
  forceRerenderFindingGridAtom,
  isQuerystringFiltersInitializedAtom,
} from './state';

export const useFindingDataConfig = (openNotesModal: (rowData) => void) => {
  const dataQueryParams = useAtomValue(findingsDataQueryParamsAtom);
  const isQuerystringFiltersInitialized = useAtomValue(
    isQuerystringFiltersInitializedAtom,
  );
  const [listingData, setListingData] = useAtom(findingsDataAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);

  const [columnKeysPC, setColumnKeysPC] = useAtom(columnKeysPCAtom);

  const forceRenderGrid = useAtomValue(forceRerenderFindingGridAtom);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      if (dataQueryParams === null) return;
      try {
        setListingData({ ...listingData, loading: true });
        const responseData = await callApi(
          `/pcm/posture/`,
          'GET',
          dataQueryParams,
        );

        setListingData({ ...responseData?.data, loading: false });
      } catch (error) {
        const errorData = (error as any)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load data.';
        showFailureToast(errorMessage);
        setListingData({
          results: [],
          insights: {
            total: 0,
            passed: 0,
            failed: 0,
            error: 0,
            exception: 0,
            urgent: 0,
            critical: 0,
            serious: 0,
            medium: 0,
            minimal: 0,
            unique_controls: 0,
            unique_assets: 0,
            unique_technologies: 0,
            unique_categories: 0,
            unique_policies: 0,
            watching_by_me_count: 0,
            unique_unread_notes_count: 0,
          },
          count: 0,
          loading: false,
        });
      } finally {
        setLoading(false);
      }
    };

    if (isQuerystringFiltersInitialized) {
      fetchData();
    }
  }, [dataQueryParams, isQuerystringFiltersInitialized]);

  const config: DataGridConfig = useMemo(
    (): DataGridConfig => ({
      rowIdKey: 'id',
      columns: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
          headerTooltip:
            'Unique Identifier for the Policy Compliance Findings in SP360 Database',
          enableCopyToClipboard: true,
        },
        {
          key: 'info_id',
          label: 'Qualys Finding ID',
          sortable: true,
          enableCopyToClipboard: true,
          headerTooltip:
            'Qualys Unique Identifier for the Policy Compliance Finding',
        },
        {
          key: 'qualys_policy_id',
          label: 'Qualys Policy ID',
          sortable: true,
          headerTooltip:
            'Unique Identifier for the Policy Compliance Finding in Qualys',
          enableCopyToClipboard: true,
        },
        {
          key: 'policy_title',
          label: 'Policy',
          sortable: true,
          headerTooltip: 'Policy Information',
          enableCopyToClipboard: true,
          rowCellClassName: 'text-nowrap',
        },
        {
          key: 'policy',
          label: 'Policy ID',
          sortable: true,
          headerTooltip: 'Policy ID',
          enableCopyToClipboard: true,
        },
        {
          key: 'control_statement',
          label: 'Statement',
          sortable: true,
          headerTooltip: 'Control Information',
          enableCopyToClipboard: true,
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', fontWeight: 'bold' },
          maxCharLength: 50,
          // rowCellClassName: 'text-nowrap',
          width: '360px',
        },
        {
          key: 'control_cid',
          label: 'CID',
          sortable: true,
          headerTooltip: 'Control ID',
          enableCopyToClipboard: true,
        },
        {
          key: 'control_category',
          label: 'Category',
          sortable: true,
          headerTooltip: 'Category',
        },
        {
          key: 'control_sub_category',
          label: 'Sub Category',
          sortable: true,
          headerTooltip: 'Sub Category',
        },
        {
          key: 'control_criticality_label',
          label: 'Criticality',
          sortable: true,
          headerTooltip: 'Criticality',
          width: '85px',
          classRules: [
            {
              className: 'text-serious',
              ruleFunction: (rowData) =>
                rowData.control_criticality_label === 'Serious',
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) =>
                rowData.control_criticality_label === 'Medium',
            },
            {
              className: 'text-urgent',
              ruleFunction: (rowData) =>
                rowData.control_criticality_label === 'Urgent',
            },
            {
              className: 'text-critical',
              ruleFunction: (rowData) =>
                rowData.control_criticality_label === 'Critical',
            },
            {
              className: 'text-minimal',
              ruleFunction: (rowData) =>
                rowData.control_criticality_label === 'Minimal',
            },
          ],
        },
        {
          key: 'host_id',
          label: 'Host ID',
          sortable: true,
          headerTooltip: 'Host Identifier',
        },
        {
          key: 'host_name',
          label: 'Host Asset',
          sortable: true,
          headerTooltip: 'Name of the associated host Asset',
          enableCopyToClipboard: true,
          rowCellStyle: { width: 'auto' },
          headerCellStyle: { width: 'auto' },
          // maxCharLength: 50,
          // width: '327px',
          overrideContents: (rowData) => {
            const alias = rowData['host_alias'];
            const name = rowData['host_name'];
            const displayName = alias || name;

            return (
              <div>
                {truncateText(displayName, 30)}
                {alias && <div className="small">{truncateText(name, 27)}</div>}
              </div>
            );
          },
          tooltip: (rowData) => {
            const alias = rowData['host_alias'];
            const name = rowData['host_name'];

            return (
              <div>
                {alias && (
                  <>
                    <strong>Alias:</strong> {truncateText(alias, 30)}
                  </>
                )}
                <div>
                  <strong>Host name:</strong> {truncateText(name, 27)}
                </div>
              </div>
            );
          },
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          headerTooltip: 'Finding Status',
          width: '55px',
          classRules: [
            {
              className: 'text-success',
              ruleFunction: (rowData) => rowData.status === 'Passed',
            },
            {
              className: 'text-serious',
              ruleFunction: (rowData) => rowData.status === 'Failed',
            },
            {
              className: 'text-critical',
              ruleFunction: (rowData) => rowData.status === 'Exception',
            },
            {
              className: 'text-urgent',
              ruleFunction: (rowData) => rowData.status === 'Error',
            },
          ],
        },
        {
          key: 'modified_date',
          label: 'Modified Date',
          sortable: true,
          headerTooltip: 'Date when the finding was last modified',
        },
        {
          key: 'technology_name',
          label: 'Technology',
          sortable: true,
          headerTooltip: 'Technology',
        },
        {
          key: 'instance',
          label: 'Instance',
          sortable: true,
          headerTooltip: 'Instance Information',
        },
        {
          key: 'evaluation_date',
          label: 'Evaluation Date',
          sortable: true,
          headerTooltip: 'Date when the finding was evaluated',
          classRules: [
            {
              className: 'text-critical',
              ruleFunction: (rowData) => {
                const evaluationDate = new Date(rowData.evaluation_date);
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - evaluationDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 20;
              },
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const evaluationDate = new Date(rowData.evaluation_date);
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - evaluationDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 10 && daysDifference <= 20;
              },
            },
          ],
          tooltip: (rowData) => {
            const evaluationDate = new Date(rowData.evaluation_date);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - evaluationDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last evaluated ${daysDifference} days ago`
              : 'Last evaluated today';
          },
        },
        {
          key: 'first_fail_date',
          label: 'First Fail Date',
          sortable: true,
          headerTooltip: 'Date when the finding first failed',
        },
        {
          key: 'last_fail_date',
          label: 'Last Fail Date',
          sortable: true,
          headerTooltip: 'Date when the finding last failed',
        },
        {
          key: 'first_pass_date',
          label: 'First Pass Date',
          sortable: true,
          headerTooltip: 'Date when the finding first passed',
        },
        {
          key: 'last_pass_date',
          label: 'Last Pass Date',
          sortable: true,
          headerTooltip: 'Date when the finding last passed',
        },
        {
          key: 'actions',
          label: 'Actions',
          headerTooltip: 'Available Actions',
          render: (rowData) => (
            <RowActions rowData={rowData} openNotesModal={openNotesModal} />
          ),
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
            whiteSpace: 'nowrap',
          },
        },
      ],
      columnsDisplayConfig: {
        enabledColumnKeys: columnKeysPC,
        defaultColumnKeys: DEFAULT_COLUMN_KEYS_PC,
        keysToBeExcluded: [],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          setColumnKeysPC(updatedColumnKeys);
        },
      },
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1,
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });
          setSelectedRowIds([]);
        },
        onPageSizeChange: (newPageSize: number) => {
          setPaginationConfig({ page_size: newPageSize, page: 1 });
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderby,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },
      rowSelectionConfig: {
        preSelectedRows: selectedRowIds,
        onSelectionChange: (updatedRowIds) => {
          setSelectedRowIds(updatedRowIds);
        },
      },
    }),
    [
      listingData,
      columnKeysPC,
      orderby,
      sortOrder,
      paginationConfig,
      selectedRowIds,
      forceRenderGrid,
    ],
  );

  return { config, data: listingData?.results, loading };
};
